import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	IconButton,
	Stack,
	Switch,
	TextField,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import { useContext, useEffect, useState, type ChangeEvent } from "react";
import { AppContext } from "../../../context/AppContextProvider";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import type { Geofence } from "./geofenceUtils";
import { t } from "i18next";

interface GeofencePanelProps {
	geofences: Geofence[];
	selectedId: string | null;
	onSelect: (id: string) => void;
	onFocusSelected: (id: string) => void;
	onAdd: () => void;
	onDelete: (id: string) => void;
	onUpdate: (id: string, patch: Partial<Geofence>) => void;
	onPickCenter: (id: string) => void;
	onSearchCenter: (id: string, query: string) => Promise<boolean>;
	isPickingCenterForId?: string | null;
}

const GeofencePanel = ({
	geofences,
	selectedId,
	onSelect,
	onFocusSelected,
	onAdd,
	onDelete,
	onUpdate,
	onPickCenter,
	onSearchCenter,
	isPickingCenterForId,
}: GeofencePanelProps) => {
	const selected = geofences.find((g) => g.id === selectedId) ?? null;
	const { requestNotificationPermission, showSnackbar } =
		useContext(AppContext);

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmTarget, setConfirmTarget] = useState<
		{ id: string; name: string } | null
	>(null);

	function handleDeleteRequest(id: string, name?: string) {
		const resolvedName = name ?? `Cerca`;
		setConfirmTarget({ id, name: resolvedName });
		setConfirmOpen(true);
	}

	const [notificationPermission, setNotificationPermission] =
		useState<NotificationPermission | null>(null);
	const [centerQuery, setCenterQuery] = useState("");
	const [isResolvingCenter, setIsResolvingCenter] = useState(false);

	useEffect(() => {
		if (typeof window !== "undefined" && "Notification" in window) {
			setNotificationPermission(window.Notification.permission);
		} else {
			setNotificationPermission(null);
		}
	}, []);

	return (
		<Card
			sx={{
				width: { xs: "min(100%, 100vw - 32px)", sm: 360 },
				height: { xs: "auto", md: "100%" },
				background:
					"linear-gradient(180deg, rgba(15, 23, 42, 0.94) 0%, rgba(30, 41, 59, 0.92) 100%)",
				color: "#e2e8f0",
				border: "1px solid rgba(148, 163, 184, 0.2)",
				boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
				backdropFilter: "blur(14px)",
				borderRadius: 3,
				overflow: "hidden",
			}}
		>
			<CardContent
				sx={{
					p: 2,
					height: "100%",
					overflowY: "auto",
					"&:last-child": { pb: 2 },
				}}
			>
				<Stack spacing={1.5} sx={{ width: "100%" }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<NotificationsActiveOutlinedIcon fontSize="small" />
						<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
							{t("map.virtualFence")}
						</Typography>
					</Box>

					<Typography variant="body2" sx={{ color: "#cbd5e1" }}>
						{t("map.virtualFenceDescription")}
					</Typography>

					<Box>
						<Button
							variant={
								notificationPermission === "granted" ? "contained" : "outlined"
							}
							size="small"
							startIcon={<NotificationsActiveOutlinedIcon />}
							onClick={async () => {
								const granted = await requestNotificationPermission();
								setNotificationPermission(granted ? "granted" : "denied");
								showSnackbar(
									granted ? t("map.notificationsEnabled") : t("map.notificationPermissionDisabled"),
									granted ? "success" : "warning",
								);
							}}
						>
							{notificationPermission === "granted"
								? t("map.notificationsEnabled")
								: t("map.notificationPermissionDisabled")}
						</Button>
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1,
							p: 1,
							borderRadius: 2,
							backgroundColor: "rgba(148, 163, 184, 0.1)",
						}}
					>
						<Box>
							<Typography variant="caption" sx={{ color: "#94a3b8" }}>
								{t("map.status")}
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{selected?.enabled ? t("map.statusMonitoring") : t("map.statusDisabled")}
							</Typography>
						</Box>
						<Switch
							checked={selected?.enabled ?? false}
							onChange={(_, checked) =>
								selected && onUpdate(selected.id, { enabled: checked })
							}
							disabled={!selected}
						/>
					</Box>

					<Divider sx={{ borderColor: "rgba(148, 163, 184, 0.18)" }} />

					<Stack spacing={1} sx={{ width: "100%" }}>
						<Box
							sx={{
								display: "flex",
								gap: 1,
								alignItems: "center",
								flexWrap: "wrap",
							}}
						>
							<Typography variant="body2" sx={{ color: "#cbd5e1" }}>
								{t("map.selectOrAddFence")}
							</Typography>
							{geofences.map((g, idx) => (
								<Chip
									key={g.id}
									label={g.name ?? `Cerca ${idx + 1}`}
									color={g.id === selectedId ? "primary" : "default"}
									onClick={() => onSelect(g.id)}
									onDelete={() => handleDeleteRequest(g.id, g.name)}
								/>
							))}

							<IconButton onClick={onAdd} size="small" sx={{ ml: 1 }}>
								<AddOutlinedIcon sx={{ color: "#60a5fa" }} />
							</IconButton>
						</Box>

						{selected ? (
							<Stack spacing={1.25} sx={{ width: "100%" }}>
								<TextField
									label={t("map.fenceName")}
									type="text"
									value={selected.name ?? ""}
									onChange={(e) =>
										onUpdate(selected.id, { name: e.target.value || undefined })
									}
									fullWidth
									sx={{
										"& .MuiInputLabel-root": { color: "#cbd5e1" },
										"& .MuiOutlinedInput-root": {
											color: "#fff",
											"& fieldset": { borderColor: "rgba(148, 163, 184, 0.3)" },
											"&:hover fieldset": {
												borderColor: "rgba(96, 165, 250, 0.8)",
											},
											"&.Mui-focused fieldset": {
												borderColor: "#60a5fa",
											},
										},
									}}
								/>

								<TextField
									label={t("map.addressOrZipCode")}
									type="text"
									value={centerQuery}
									onChange={(event: ChangeEvent<HTMLInputElement>) =>
										setCenterQuery(event.target.value)
									}
									fullWidth
									sx={{
										"& .MuiInputLabel-root": { color: "#cbd5e1" },
										"& .MuiOutlinedInput-root": {
											color: "#fff",
											"& fieldset": { borderColor: "rgba(148, 163, 184, 0.3)" },
											"&:hover fieldset": {
												borderColor: "rgba(96, 165, 250, 0.8)",
											},
											"&.Mui-focused fieldset": {
												borderColor: "#60a5fa",
											},
										},
									}}
								/>

								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={1}
									alignItems="stretch"
									width="100%"
								>
									<Button
										variant="contained"
										size="small"
										fullWidth
										disabled={
											!selected || !centerQuery.trim() || isResolvingCenter
										}
										onClick={async () => {
											if (!selected) return;
											setIsResolvingCenter(true);
											try {
												const resolved = await onSearchCenter(
													selected.id,
													centerQuery.trim(),
												);
												if (resolved) setCenterQuery("");
											} finally {
												setIsResolvingCenter(false);
											}
										}}
										sx={{
											background:
												"linear-gradient(90deg, #2563eb 0%, #0f766e 100%)",
											color: "#fff",
											textTransform: "none",
											fontWeight: 700,
											"&:hover": {
												background:
													"linear-gradient(90deg, #1d4ed8 0%, #115e59 100%)",
											},
										}}
									>
										{isResolvingCenter
											? t("map.searchingAddress")
											: t("map.searchAddressCenter")}
									</Button>

									<Typography
										variant="caption"
										sx={{
											color: "#94a3b8",
											textAlign: "center",
											alignSelf: "center",
											whiteSpace: "nowrap",
										}}
									>
										Ou
									</Typography>

									<Button
										variant="contained"
										startIcon={<RoomOutlinedIcon />}
										fullWidth
										onClick={() => onPickCenter(selected.id)}
										sx={{
											justifyContent: "center",
											textTransform: "none",
											fontWeight: 700,
											backgroundColor: "transparent",
											color: "#cbd5e1",
											border: "1px solid rgba(96, 165, 250, 0.35)",
											"&:hover": {
												backgroundColor: "rgba(96, 165, 250, 0.12)",
											},
										}}
									>
										{isPickingCenterForId === selected.id
											? t("map.pickingAddress")
											: t("map.pickAddressCenter")}
									</Button>
								</Stack>

								<TextField
									label={t("map.radiusMeters")}
									type="number"
									value={selected.radiusMeters}
									onChange={(event) =>
										onUpdate(selected.id, {
											radiusMeters: Math.max(
												50,
												Number(event.target.value) || 0,
											),
										})
									}
									inputProps={{ min: 50, step: 10 }}
									fullWidth
									sx={{
										"& .MuiInputLabel-root": { color: "#cbd5e1" },
										"& .MuiOutlinedInput-root": {
											color: "#fff",
											"& fieldset": { borderColor: "rgba(148, 163, 184, 0.3)" },
											"&:hover fieldset": {
												borderColor: "rgba(96, 165, 250, 0.8)",
											},
											"&.Mui-focused fieldset": {
												borderColor: "#60a5fa",
											},
										},
									}}
								/>
							</Stack>
						) : null
					}</Stack>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1,
							p: 1,
							borderRadius: 2,
							backgroundColor: "rgba(15, 23, 42, 0.35)",
						}}
					>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="caption" sx={{ color: "#94a3b8" }}>
								Centro
							</Typography>
							<Typography
								variant="body2"
								sx={{ fontWeight: 600, wordBreak: "break-word" }}
							>
								{selected?.center
									? `${selected.center.latitude.toFixed(4)}, ${selected.center.longitude.toFixed(4)}`
									: t("map.empty")}
							</Typography>
						</Box>
						<Chip
							size="small"
							icon={<MyLocationOutlinedIcon />}
							label={selected?.center ? t("map.centerMarked") : t("map.centerEmpty")}
							onClick={() => {
								if (selected?.center) {
									onFocusSelected(selected.id);
								}
							}}
							clickable={Boolean(selected?.center)}
							sx={{
								color: selected?.center ? "#86efac" : "#cbd5e1",
								cursor: selected?.center ? "pointer" : "default",
							}}
						/>
					</Box>

					<Box
						sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
					>
						<Button
							variant="text"
							startIcon={<DeleteOutlineOutlinedIcon />}
							onClick={() =>
								selected &&
								onUpdate(selected.id, { center: null, enabled: false })
							}
							sx={{
								color: "#fecaca",
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							{t("map.clearCenter")}
						</Button>
						<Typography
							variant="caption"
							sx={{ alignSelf: "center", color: "#94a3b8" }}
						>
							{selected?.enabled && selected?.center
								? t("map.geofenceActive")
								: t("map.geofenceInactive")}
						</Typography>
					</Box>
				</Stack>
			</CardContent>
			<Dialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				aria-labelledby="confirm-delete-dialog"
			>
				<DialogTitle id="confirm-delete-dialog">
					{t("map.confirmDeleteFenceTitle")}
				</DialogTitle>
				<DialogContent dividers>
					{confirmTarget
						? t("map.confirmDeleteFence", { name: confirmTarget.name }) ||
							`Deseja excluir cerca "${confirmTarget.name}"?`
						: "Deseja excluir cerca?"}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmOpen(false)}>
						{t("map.cancel")}
					</Button>
					<Button
						color="error"
						onClick={() => {
							if (confirmTarget) onDelete(confirmTarget.id);
							setConfirmOpen(false);
						}}
					>
						{t("map.delete")}
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};

export default GeofencePanel;
